import { Note, NoteRange } from "../music/note";
import { Piano } from "./piano";

export class Viewport {
  private from: Note;
  private to: Note;

  constructor(
    private readonly piano: Piano,
    initialWindow: NoteRange
  ) {
    if (!piano.contains(initialWindow.from) || !piano.contains(initialWindow.to)) {
      throw new Error('Viewport must be inside piano range');
    }

    this.from = initialWindow.from;
    this.to = initialWindow.to;
  }

  get window(): NoteRange {
    return new NoteRange(this.from, this.to);
  }

  setWindow(window: NoteRange): void {
    if (!this.piano.contains(window.from) || !this.piano.contains(window.to)) {
      throw new Error('Viewport must be inside piano range');
    }

    this.from = window.from;
    this.to = window.to;
  }

  size(): number {
    return this.to.toMidi() - this.from.toMidi() + 1;
  }

  pan(semitones: number): void {
    this.setFromKeepingSize(
      Note.fromMidi(this.from.toMidi() + semitones),
      this.size()
    );
  }

  centerOn(note: Note): void {
    const half = Math.floor(this.size() / 2);
    this.setFromKeepingSize(
      Note.fromMidi(note.toMidi() - half),
      this.size()
    );
  }

  /**
   * Center the viewport on a MIDI note range [minMidi, maxMidi],
   * resizing the window to fit the range plus a margin on each side.
   *
   * @param minMidi  Lowest MIDI note to include
   * @param maxMidi  Highest MIDI note to include
   * @param margin   Extra semitones of padding on each side (default: 4)
   * @param minSize  Minimum viewport size in semitones (default: 48, ~4 octaves)
   */
  centerOnRange(minMidi: number, maxMidi: number, margin = 4, minSize = 48): void {
    const world = this.piano.getRange();
    const worldFrom = world.from.toMidi();
    const worldTo = world.to.toMidi();
    const maxSize = worldTo - worldFrom + 1;

    // Required size to cover the range + margins
    const requiredSize = (maxMidi - minMidi + 1) + margin * 2;
    // Clamp to [minSize, maxSize]
    const size = Math.max(minSize, Math.min(requiredSize, maxSize));

    // Center between min and max
    const center = Math.floor((minMidi + maxMidi) / 2);
    const half = Math.floor(size / 2);

    this.setFromKeepingSize(
      Note.fromMidi(Math.max(0, center - half)),
      size
    );
  }

  zoom(newSize: number): void {
    const maxSize = this.piano.getRange().size();
    const size = Math.min(newSize, maxSize);

    const center = Math.floor((this.from.toMidi() + this.to.toMidi()) / 2);
    const half = Math.floor(size / 2);

    this.setFromKeepingSize(
      Note.fromMidi(center - half),
      size
    );
  }

  *visibleNotes(): IterableIterator<Note> {
    yield* this.window.notes();
  }

  private setFromKeepingSize(desiredFrom: Note, size: number) {
    const world = this.piano.getRange();
    const worldFrom = world.from.toMidi();
    const worldTo = world.to.toMidi();

    let fromMidi = desiredFrom.toMidi();
    let toMidi = fromMidi + size - 1;

    if (fromMidi < worldFrom) {
      fromMidi = worldFrom;
      toMidi = fromMidi + size - 1;
    }

    if (toMidi > worldTo) {
      toMidi = worldTo;
      fromMidi = toMidi - size + 1;
    }

    this.from = Note.fromMidi(fromMidi);
    this.to = Note.fromMidi(toMidi);
  }
}
